import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { AgRendererComponent } from 'ag-grid-angular/main';
import { ICellRendererParams } from 'ag-grid-community/main';
import { orderBy } from 'lodash';
import { combineLatest, Subscription } from 'rxjs';

import { LanguagesStore, Settings } from '../../core';
import { ELearningOpener } from '../../e-learning/e-learning-opener';
import { UserStorage } from '../../user/user-storage';
import { DocumentDownloader } from '../../shared/document-downloader/document-downloader';
import { Category } from '../models/category';
import { LinkByFile } from '../models/dlLink';
import { DocumentsCounterBase } from '../models/documents-counter';
import { UnreadReportsStorage, UNREAD_REPORTS_STORAGE } from '../unread-reports-storage';
import { FileLink, Report } from './../models/report';
import { PlayVideoDialogComponent, VideoDialogParameters, VIDEO_DIALOG_PARAMETERS } from './play-video-dialog.component';
import { PreviewDialogComponent, PREVIEW_TOKEN } from './preview-dialog.component';

export interface LanguageLinkParams {
    isLanguageSelectionDisabled: boolean;
}

type GridRow = {
    data: Report,
    api: {
        redrawRows: () => void
    },
};

@Component({
    selector: 'language-link-cell',
    template: require('./language-link-ag-renderer.component.html'),
    styles: [require('./language-link-ag-renderer.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LanguageLinkAgRendererComponent implements AgRendererComponent, OnDestroy {
    public languages: FileLink[] = [];
    public preferredLanguage: FileLink;
    public isLanguageSelectionDisabled: boolean;
    public isPreviewAvailable: boolean;

    public get hasData() {
        return this.row && this.row.data;
    }

    private subscription: Subscription;
    private report: Report;
    private row: GridRow;

    constructor(
        private userStorage: UserStorage,
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(UNREAD_REPORTS_STORAGE) private unreadReportsRegistry: UnreadReportsStorage<DocumentsCounterBase>,
        private languagesStore: LanguagesStore,
        private documentDownloader: DocumentDownloader,
        private dialogService: MdlDialogService,
        private settings: Settings,
        private readonly eLearningOpener: ELearningOpener
    ) {
        this.preferredLanguage = new FileLink();
        this.preferredLanguage.fileId = '';
        this.preferredLanguage.language = '';
        this.preferredLanguage.fileName = '';
    }

    public refresh() {
        return false;
    }

    public agInit(row: ICellRendererParams): void {
        if (!row.data) {
            return;
        }
        this.isLanguageSelectionDisabled = row.value && row.value.isLanguageSelectionDisabled;

        this.subscription = combineLatest(this.languagesStore.orderLanguage, this.userStorage.currentUser)
            .subscribe(([orderFunc, u]) => {

                this.row = row;
                this.report = row.data as Report;
                this.languages = orderBy(this.report.fileLinks, orderFunc) || [];
                this.preferredLanguage = (this.languages.length > 1) ?
                    this.languages.find(l => l.languageId === u.settings.preferredLanguageId) || this.languages[0]
                    : this.languages[0];
                this.isPreviewAvailable = this.report.isPreviewAvailable;
                this.changeDetectorRef.markForCheck();
            });
    }

    public ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public previewDocument(event: Event) {
        event.stopPropagation();

        const dlLink = new LinkByFile(
            this.preferredLanguage.fileId,
            this.report.reportId,
            this.report.fileLinkCategory
        );

        const configDialog: IMdlCustomDialogConfiguration = {
            component: PreviewDialogComponent,
            providers: [{
                provide: PREVIEW_TOKEN, useValue: dlLink
            }],
            isModal: true,
            clickOutsideToClose: true
        };

        this.dialogService.showCustomDialog(configDialog);
    }

    public async onLinkClick(fileLink: FileLink) {
        const dlLink = new LinkByFile(
            fileLink.fileId,
            this.report.reportId,
            this.report.fileLinkCategory
        );

        const fileExtension = `.${fileLink.fileName.split('.').pop()}`.toLowerCase();
        const isPlayableVideoFile = this.settings.playableVideoFileExtensions.some(e => e.toLowerCase() === fileExtension);

        if (isPlayableVideoFile) {
            await this.openPlayVideoDialog(dlLink);
        } else if (Category.isELearningCategory(dlLink.categoryNaturalKey)) {
            await this.openELearning(dlLink);
        } else {
            await this.downloadFile(dlLink);
        }
    }

    private async openPlayVideoDialog(dlLink: LinkByFile) {
        const url = await this.documentDownloader.getVideoUrl(dlLink);
        if (url) {
            await this.dialogService.showCustomDialog({
                component: PlayVideoDialogComponent,
                providers: [{
                    provide: VIDEO_DIALOG_PARAMETERS,
                    useValue: {
                        videoUrl: url,
                        videoLoaded: () => this.markAsRead()
                    } as VideoDialogParameters
                }],
                isModal: true,
                clickOutsideToClose: true
            });

        }
    }

    private async openELearning(dlLink: LinkByFile) {
        const success = await this.eLearningOpener.open(dlLink);
        if (success) {
            await this.markAsRead();
            this.refreshView();
        }
    }

    private async downloadFile(dlLink: LinkByFile) {
        const success = await this.documentDownloader.downloadDocumentByUrl(dlLink);

        if (success) {
            await this.markAsRead();
            this.refreshView();
        }
    }

    private async markAsRead() {
        await this.unreadReportsRegistry.markAsRead([this.report]);
    }

    private refreshView() {
        this.row.api.redrawRows();
    }
}